/** Barème RPS interne et fixe. Les valeurs saisies pourront ensuite venir de l'enquête. */
export interface PopulationRpsOptions {
  severity: number | null //Pourcentage de personnes concernees, de 0 a 100
  frequency: number | null //Exposition, de 0 (jamais) a 4 (toujours)
  prevention: number | null //Maitrise, de 1 (aucune) a 5 (suivie et ajustee)
  manualLevel: number | null //Niveau global saisi, null = calcul automatique
}

export const createRpsOptions = (): PopulationRpsOptions => ({ severity: null, frequency: null, prevention: null, manualLevel: null })

//La valeur est la part retenue pour le calcul, en pourcentage
export const RPS_PERSONS_SCALE = [
  { value: 0, label: "Aucun cas", description: "" },
  { value: 15, label: "Quelques cas", description: "Une ou deux personnes isolées." },
  { value: 35, label: "Plusieurs cas", description: "Une part non négligeable des personnes est en situation de RPS." },
  { value: 75, label: "De nombreux cas", description: "La plupart des personnes de la fonction sont concernées." }
] as const

export const RPS_FREQUENCY_SCALE = [
  { value: 0, label: "Jamais", description: "Les personnes ne rencontrent pas ces situations." },
  { value: 1, label: "Rarement", description: "Quelques fois par an." },
  { value: 2, label: "Parfois", description: "Quelques fois par mois." },
  { value: 3, label: "Souvent", description: "Chaque semaine." },
  { value: 4, label: "Toujours", description: "Tous les jours ou presque." }
] as const

export const RPS_PREVENTION_SCALE = [
  { value: 1, label: "Aucune", description: "Aucune mesure en place." },
  { value: 2, label: "Insuffisante", description: "Quelques actions isolées, sans effet réel sur les situations." },
  { value: 3, label: "Partielle", description: "Des mesures existent mais ne couvrent pas toutes les situations." },
  { value: 4, label: "Bonne", description: "Les situations identifiées sont couvertes par des mesures adaptées." },
  { value: 5, label: "Très bonne", description: "Les mesures sont suivies, évaluées et ajustées régulièrement." }
] as const

//Score sur 100 avant prevention : part de personnes concernees × exposition normalisee.
//Comme en PHY, la prevention divise le score. Les seuils RPS sont fixes, hors parametres du compte.
export const RPS_GLOBAL_THRESHOLDS = [5, 15, 35] as const

export function calculateRps(options?: Partial<PopulationRpsOptions> | null): { score: number | null; level: number | null } {
  const { severity, frequency, prevention } = options ?? {}
  if (severity == null || frequency == null || prevention == null) return { score: null, level: null }
  if (!Number.isFinite(severity) || severity < 0 || severity > 100 ||
    !RPS_FREQUENCY_SCALE.some((item) => item.value === frequency) ||
    !RPS_PREVENTION_SCALE.some((item) => item.value === prevention)) return { score: null, level: null }

  const score = severity * (frequency / RPS_FREQUENCY_SCALE[RPS_FREQUENCY_SCALE.length - 1].value) / prevention
  const index = RPS_GLOBAL_THRESHOLDS.findIndex((threshold) => score <= threshold)
  return { score, level: index === -1 ? RPS_GLOBAL_THRESHOLDS.length : index }
}

/** Un choix manuel prend le pas sur le calcul, sans effacer les trois critères. */
export function evaluateRps(options?: Partial<PopulationRpsOptions> | null): { score: number | null; level: number | null } {
  const calculated = calculateRps(options)
  const manualLevel = options?.manualLevel
  if (manualLevel == null) return calculated
  return { score: calculated.score, level: Number.isInteger(manualLevel) && manualLevel >= 0 && manualLevel <= 3 ? manualLevel : null }
}
